import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import api from '../lib/api';
import Markdown from '../components/Markdown';
import TopBar, { TBTN } from '../components/Wiki/TopBar';
import WikiLayout, { DOC_WIDE } from '../layouts/WikiLayout';
import type {
  WikiPageSummary,
  WikiPageVersionFull,
  WikiPageVersionSummary,
} from '../types/wiki';

type WikiVersionsProps = {
  page: WikiPageSummary;
  versions: WikiPageVersionSummary[];
  selected: WikiPageVersionFull | null;
  showingAll: boolean;
};

type WikiVersionsComponent = React.FC<WikiVersionsProps> & {
  layout?: (page: React.ReactNode) => React.ReactNode;
};

const WHO: Record<string, string> = { human: '你', agent: 'Claude' };

// label 是「这一版是怎么产生的」（§6.2 修订）。翻成人话；认不出的原样显示 ——
// 将来加了新 label 忘了翻译，看到英文原文也比看到空白强。
const EVENT: Record<string, string> = {
  initial: '建页',
  save: '保存',
  'auto save': '自动保存',
  'mcp write': 'Claude 写入',
  'api write': 'API 写入',
};

const eventName = (label: string | null) => {
  if (!label) return '';
  if (EVENT[label]) return EVENT[label];
  // rollback to v3 那一类：数字会变，查不到表，按前缀认。
  const rolled = label.match(/^rollback to v(\d+)$/);
  return rolled ? `回滚自 v${rolled[1]}` : label;
};

const stamp = (iso: string) =>
  new Date(iso).toLocaleString('zh-CN', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

// 版本历史是**偶尔进来一次**的地方，不是读正文时一直要看的东西 —— 所以它是
// 一条独立路由，不是塞进阅读版式里的侧栏。
//
// 改版把它从「列表在上、全文在下」改成设计稿那个**双栏**（左边挑版本、右边看
// 那一版）。理由是这一页的动作是**比对**：改版前选一版要滚到下面读，再选下一版
// 又要滚回上面，而两版之间的差别恰恰要靠来回切才看得出来。双栏之后左边那一列
// 不动，切换只换右边。
//
// 这一期仍然没有 diff 高亮（§6.5）：要引 jsdiff 那类库和一套 diff 视图组件，
// 而「切着看」在几 KB 的文档上是可用的 —— 双栏正是让「切着看」变得便宜。
const WikiVersions: WikiVersionsComponent = ({ page, versions, selected, showingAll }) => {
  const [rollingBack, setRollingBack] = useState(false);

  // 两个筛选维度（看哪一版、要不要列自动保存）都在 URL 里，所以每个链接都得把
  // **另一个**维度带上。
  //
  // 一开始版本链接只写 ?v=N，结果是：开着「显示自动保存」去点一条自动版本，
  // all=1 掉了 → 列表collapse 回只剩保存点、那一条从列表里消失，而它的正文还在
  // 下面显示着。等于在读一个列表里不存在的版本。浏览器里点出来的。
  const hrefFor = ({ v, all }: { v?: number; all: boolean }) => {
    const q = new URLSearchParams();
    if (v !== undefined) q.set('v', String(v));
    if (all) q.set('all', '1');
    const query = q.toString();
    return `/wiki/${page.url_key}/versions${query ? `?${query}` : ''}`;
  };

  const rollback = async (version: number) => {
    if (!window.confirm(`把这一页回滚到第 ${version} 版？当前内容会作为新的一版留在历史里。`)) {
      return;
    }

    setRollingBack(true);
    try {
      await api.wikiPages.rollback(page.url_key, version);
      // 回滚完回正文页 —— 那里才看得到结果。
      router.visit(`/wiki/${page.url_key}`);
    } finally {
      setRollingBack(false);
    }
  };

  return (
    <>
      <TopBar
        trail={[
          { label: page.title, href: `/wiki/${page.url_key}` },
          { label: '历史' },
        ]}
        actions={
          // 自动版本默认不列（§6.2 修订）：它们是防丢失的，不是给人读的。
          //
          // 长得像开关，但**仍然是一个 Link**：状态在 URL 里（上面 hrefFor 那段
          // 注释论证过为什么两个维度必须互相带上）。做成受控 checkbox 就得再
          // 造一份组件 state，而那份 state 与 URL 之间会漂。
          <Link href={hrefFor({ v: selected?.version, all: !showingAll })} className={TBTN}>
            <span className="flex items-center gap-1.5">
              <span
                className={`relative h-[15px] w-[26px] rounded-full transition-colors ${
                  showingAll ? 'bg-todo-accent' : 'bg-todo-line2'
                }`}
              >
                <span
                  className={`absolute top-[2px] h-[11px] w-[11px] rounded-full bg-white
                              transition-all ${showingAll ? 'left-[13px]' : 'left-[2px]'}`}
                />
              </span>
              显示自动保存
            </span>
          </Link>
        }
      />

      <div className={DOC_WIDE}>
        <h1 className="mb-5 text-[26px] font-bold tracking-tight">版本历史</h1>

        {/* 窄屏退回单列：260px 的列表 + 一栏正文在手机上装不下，而这一页在手机上
            仍然要能用（「哪一版是我改的」是个随时会问的问题）。 */}
        <div className="grid grid-cols-1 gap-6 wide:grid-cols-[260px_minmax(0,1fr)] wide:gap-7">
          <div>
            {versions.map((v) => {
              const on = selected?.version === v.version;
              return (
                <Link
                  key={v.version}
                  href={hrefFor({ v: v.version, all: showingAll })}
                  preserveScroll
                  className={`-mx-2.5 flex items-baseline gap-2 rounded-[7px] px-2.5 py-2 ${
                    on ? 'bg-todo-accent2' : 'hover:bg-wiki-sideHover'
                  }`}
                >
                  <span className="w-[34px] shrink-0 text-[12.5px] text-todo-muted">
                    v{v.version}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13.5px] text-todo-ink">
                      {WHO[v.author_kind] ?? v.author_kind} 改的
                    </span>
                    <span className="mt-px block text-[12px] text-todo-muted">
                      {eventName(v.label)} · {stamp(v.created_at)}
                    </span>
                  </span>
                </Link>
              );
            })}
          </div>

          {/* 右栏。没选任何一版时不留一个空框 —— 一个带边框的空白区域看起来像
              「加载失败」，一句话更清楚。 */}
          {selected ? (
            <div className="rounded-[10px] border border-todo-line bg-todo-card px-6 py-5">
              <div className="mb-3.5 flex flex-wrap items-baseline gap-2.5">
                <div className="text-[17px] font-semibold">第 {selected.version} 版</div>
                <div className="text-[12.5px] text-todo-muted">
                  {WHO[selected.author_kind] ?? selected.author_kind} 改的 ·{' '}
                  {eventName(selected.label)} · {stamp(selected.created_at)}
                </div>
                {/* 当前那一版没有「回滚到它」的意义 —— 它就是现在的内容。 */}
                {selected.version !== page.version && (
                  <button
                    type="button"
                    onClick={() => rollback(selected.version)}
                    disabled={rollingBack}
                    className="ml-auto shrink-0 rounded-md border border-todo-line px-2.5 py-1
                               text-[12.5px] text-todo-ink2 hover:bg-todo-card2
                               disabled:opacity-50"
                  >
                    {rollingBack ? '回滚中…' : '回滚到这一版'}
                  </button>
                )}
              </div>

              <h2 className="mb-3 text-[19px] font-semibold tracking-tight">{selected.title}</h2>

              {selected.body_md ? (
                <Markdown size="article" className="text-todo-ink">
                  {selected.body_md}
                </Markdown>
              ) : (
                <p className="text-[13.5px] text-todo-muted">这一版没有正文。</p>
              )}

              {/* 设计稿这一句。它回答的是「点下去会不会丢东西」，而那是按下
                  「回滚」之前唯一想知道的事 —— 写在确认框里太晚，那时人已经
                  决定了。 */}
              <div className="mt-4 border-t border-todo-line pt-2.5 text-[12.5px] text-todo-muted">
                回滚不删历史：它拿这一版的内容写出新的一版，所以回滚本身也能被回滚。
              </div>
            </div>
          ) : (
            <p className="text-[13.5px] text-todo-muted">
              左边点一版看它的全文。
              {versions.length === 0 && ' 这一页还没有版本记录。'}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

WikiVersions.layout = (page: React.ReactNode) => <WikiLayout>{page}</WikiLayout>;

export default WikiVersions;
